"use client";

// Tremor Raw Input [v1.0.0]
import { type VariantProps } from "tailwind-variants";

import React, { useCallback, useEffect, useState } from "react";
import { RiEyeFill, RiEyeOffFill, RiSearchLine } from "react-icons/ri";

import AIBadge from "@/components/ui/ai-badge";
import { AIToolbar } from "@/components/ui/ai-toolbar";

import { useAIGenerationWithStream } from "@/hooks/use-ai-generation-with-stream";

import { tv } from "@/lib/tailwind-utils";
import { cx, focusInput, focusRing, hasErrorInput } from "@/lib/tailwind-utils";

const inputStyles = tv({
  base: [
    // base
    "relative block w-full appearance-none rounded-xl border px-2.5 py-2 shadow-sm outline-none transition text-body",
    // border color
    "border-border",
    // text color
    "text-foreground",
    // placeholder color
    "placeholder-foreground-muted",
    // background color
    "!bg-transparent",
    // disabled
    "disabled:!bg-muted disabled:text-foreground-muted",
    // file
    [
      "file:-my-1.5 file:-ml-2.5 file:h-[36px] file:cursor-pointer file:rounded-l-md file:rounded-r-none file:border-0 file:px-3 file:py-1.5 file:outline-none focus:outline-none disabled:pointer-events-none file:disabled:pointer-events-none",
      "file:[border-inline-end-width:1px] file:[border-inline-end-style:solid] file:[border-inline-end-color:rgb(var(--border))] dark:file:[border-inline-end-color:rgb(var(--border))] file:[margin-inline-end:0.75rem]",
      "file:!bg-background-secondary file:text-muted-foreground file:hover:!bg-hover",
      "file:disabled:!bg-muted file:disabled:text-muted-foreground",
    ],
    // focus
    focusInput,
  ],
  variants: {
    hasError: {
      true: hasErrorInput,
    },
    // number input
    enableStepper: {
      false:
        "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
    },
  },
});

interface InputProps
  extends
    Omit<React.InputHTMLAttributes<HTMLInputElement>, "value">,
    VariantProps<typeof inputStyles> {
  value?: string | number | readonly string[] | null;
  errorMessage?: string;
  hasError?: boolean;
  enableStepper?: boolean;
  autocomplete?: "on" | "off";
  characterLimit?: number;
  /** Set to false when a numeric field should retain zeros before a decimal point. */
  stripLeadingZeros?: boolean;
  icon?: React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;
  /** If provided, enables AI suggestion features. Function returns an async generator. */
  onGenerateAI?: (
    currentValue: string,
    feedback?: string,
    previousGeneration?: string,
  ) => AsyncGenerator<string>;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (inputProps: InputProps, forwardedRef) => {
    const {
      className,
      hasError,
      errorMessage,
      enableStepper,
      type,
      autocomplete = "on",
      characterLimit,
      stripLeadingZeros = true,
      onChange,
      onBlur,
      value,
      icon: Icon,
      onGenerateAI,
      disabled,
      "aria-describedby": ariaDescribedBy,
      ...props
    } = inputProps;
    const [typeState, setTypeState] = React.useState(type);
    const isPassword = type === "password";
    const errorId = React.useId();
    const describedBy =
      [ariaDescribedBy, hasError ? errorId : undefined]
        .filter(Boolean)
        .join(" ") || undefined;
    const internalRef = React.useRef<HTMLInputElement>(null);

    const ref = useCallback(
      (node: HTMLInputElement | null) => {
        internalRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    useEffect(() => {
      const input = internalRef.current;
      if (!input || type !== "number") return;

      const handleWheel = (e: WheelEvent) => {
        if (document.activeElement === input) {
          e.preventDefault();
        }
      };

      input.addEventListener("wheel", handleWheel, { passive: false });
      return () => {
        input.removeEventListener("wheel", handleWheel);
      };
    }, [type]);

    // Callers store a number, so a cleared number field comes back as 0 (or
    // their minimum) and would show that again: backspacing "3" and typing
    // "1" gave "10". While the user edits, keep what they typed if it is the
    // same number as the caller's value ("1." for 1), or empty and the caller
    // has not changed the value since. A value the caller capped or reset is
    // shown as is, and blur always shows the caller's value.
    const [typed, setTyped] = useState<string | null>(null);
    const [valueWhenTyped, setValueWhenTyped] = useState({ typed, value });
    if (valueWhenTyped.typed !== typed) setValueWhenTyped({ typed, value });
    const keepTyped =
      typed !== null &&
      (Number(typed) === Number(value) ||
        (typed === "" && Object.is(valueWhenTyped.value, value)));
    // A number field given a `value` prop stays controlled when that value
    // is null or undefined (an unset number), so callers don't need `?? ""`.
    const controlled =
      value !== undefined || (type === "number" && "value" in inputProps);
    const shownValue = controlled
      ? keepTyped
        ? typed
        : (value ?? "")
      : undefined;
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setTyped(null);
      onBlur?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (characterLimit && e.target.value.length > characterLimit) {
        e.target.value = e.target.value.slice(0, characterLimit);
      }

      if (type === "number" && stripLeadingZeros && e.target.value) {
        const numericValue = e.target.value.replace(/^0+(?=\d)/, "") || "0";
        if (numericValue !== e.target.value) {
          e.target.value = numericValue;
        }
      }

      if (type === "number") setTyped(e.target.value);

      onChange?.(e);
    };

    // AI state (only used if onGenerateAI is provided)
    const [suggestionMode, setSuggestionMode] = useState(false);
    const [feedback, setFeedback] = useState("");
    const [feedbackHistory, setFeedbackHistory] = useState<string[]>([]);
    const [editedText, setEditedText] = useState("");

    const aiGeneration = useAIGenerationWithStream({
      onTextUpdate: (text) => {
        setEditedText(text);
      },
    });

    const aiIsGenerating = aiGeneration.isGenerating;
    const aiGeneratedText = aiGeneration.generatedText;
    const aiError = aiGeneration.error;
    const aiCancel = aiGeneration.cancel;
    const aiReset = () => {
      setEditedText("");
      aiGeneration.reset();
    };

    // Sync edited text when generation updates
    useEffect(() => {
      if (onGenerateAI && aiGeneratedText) {
        setEditedText(aiGeneratedText);
      }
    }, [onGenerateAI, aiGeneratedText]);

    const handleGenerate = useCallback(async () => {
      if (!onGenerateAI) return;
      setSuggestionMode(true);
      setFeedback("");
      setFeedbackHistory([]);
      setEditedText("");
      await aiGeneration.generate(() => onGenerateAI((value as string) || ""));
    }, [onGenerateAI, value, aiGeneration]);

    const handleRetry = useCallback(async () => {
      if (!onGenerateAI) return;
      await aiGeneration.generate(() => onGenerateAI((value as string) || ""));
    }, [onGenerateAI, value, aiGeneration]);

    const handleRegenerate = useCallback(async () => {
      if (feedback.trim()) {
        setFeedbackHistory((prev) => [...prev, feedback.trim()]);
      }
      if (!onGenerateAI) return;
      await aiGeneration.regenerate(
        onGenerateAI,
        (value as string) || "",
        feedback,
        editedText,
      );
      setFeedback("");
    }, [feedback, onGenerateAI, value, editedText, aiGeneration]);

    const handleAccept = useCallback(() => {
      // Trigger onChange with a properly constructed event
      if (onChange && internalRef.current) {
        const input = internalRef.current;
        input.value = editedText;
        const event = {
          target: input,
          currentTarget: input,
          bubbles: true,
          cancelable: true,
          defaultPrevented: false,
          eventPhase: 0,
          isTrusted: false,
          nativeEvent: new Event("change", { bubbles: true }),
          preventDefault: () => {},
          stopPropagation: () => {},
          isDefaultPrevented: () => false,
          isPropagationStopped: () => false,
          persist: () => {},
          timeStamp: Date.now(),
          type: "change",
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event);
      }
      setSuggestionMode(false);
      setFeedback("");
      setFeedbackHistory([]);
      setEditedText("");
      aiReset();
    }, [onChange, editedText, aiReset]);

    // If onGenerateAI is provided, render with AI features
    if (onGenerateAI) {
      return (
        <>
          <div
            className={cx(
              "border-border overflow-hidden rounded-lg border bg-background transition-all",
              "relative",
              suggestionMode && "shadow-sm",
            )}
          >
            {/* Content area */}
            {suggestionMode ? (
              <div>
                {aiError ? (
                  <div className="p-3 text-body text-red-600">
                    Error: {aiError}
                  </div>
                ) : aiIsGenerating ? (
                  <div className="whitespace-pre-wrap p-3 text-body text-gray-900">
                    {aiGeneratedText || (
                      <span className="text-gray-400">Generating...</span>
                    )}
                    <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-primary dark:bg-primary-400" />
                  </div>
                ) : (
                  <input
                    ref={ref}
                    type={isPassword ? typeState : type}
                    value={editedText}
                    onChange={(e) => {
                      if (
                        characterLimit &&
                        e.target.value.length > characterLimit
                      ) {
                        e.target.value = e.target.value.slice(
                          0,
                          characterLimit,
                        );
                      }
                      if (
                        type === "number" &&
                        stripLeadingZeros &&
                        e.target.value
                      ) {
                        const numericValue =
                          e.target.value.replace(/^0+(?=\d)/, "") || "0";
                        if (numericValue !== e.target.value) {
                          e.target.value = numericValue;
                        }
                      }
                      setEditedText(e.target.value);
                    }}
                    className="text-foreground w-full rounded-none border-0 bg-transparent px-3 py-2 text-body shadow-none outline-none ring-0 focus:shadow-none focus:outline-none focus:ring-0"
                    placeholder="AI generated content will appear here..."
                    aria-describedby={describedBy}
                    {...props}
                  />
                )}
              </div>
            ) : (
              <div className="relative">
                <input
                  ref={ref}
                  type={isPassword ? typeState : type}
                  className={cx(
                    "text-foreground w-full rounded-none border-0 bg-transparent px-3 py-2 text-body shadow-none outline-none ring-0 focus:shadow-none focus:outline-none focus:ring-0",
                    {
                      "pl-8": !!Icon,
                      "pr-10": isPassword,
                    },
                  )}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={shownValue}
                  disabled={disabled}
                  aria-describedby={describedBy}
                  {...props}
                  autoComplete={autocomplete}
                />
                {Icon && (
                  <div
                    className={cx(
                      "pointer-events-none absolute bottom-0 left-2 flex h-full items-center justify-center",
                      "text-foreground-muted",
                    )}
                  >
                    <Icon
                      className="size-[1.125rem] shrink-0"
                      aria-hidden={true}
                    />
                  </div>
                )}
                {isPassword && (
                  <div
                    className={cx(
                      "absolute bottom-0 right-0 flex h-full items-center justify-center px-3",
                    )}
                  >
                    <button
                      aria-label="Change password visibility"
                      className={cx(
                        "h-fit w-fit rounded-sm outline-none transition-all",
                        "text-foreground-muted",
                        "hover:text-muted-foreground",
                        focusRing,
                      )}
                      type="button"
                      onClick={() => {
                        setTypeState(
                          typeState === "password" ? "text" : "password",
                        );
                      }}
                    >
                      <span className="sr-only">
                        {typeState === "password"
                          ? "Show password"
                          : "Hide password"}
                      </span>
                      {typeState === "password" ? (
                        <RiEyeFill
                          aria-hidden="true"
                          className="size-5 shrink-0"
                        />
                      ) : (
                        <RiEyeOffFill
                          aria-hidden="true"
                          className="size-5 shrink-0"
                        />
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* AI Toolbar */}
            {suggestionMode ? (
              <AIToolbar
                isGenerating={aiIsGenerating}
                hasError={!!aiError}
                hasGeneratedText={!!editedText}
                feedbackHistoryCount={feedbackHistory.length}
                feedback={feedback}
                onFeedbackChange={setFeedback}
                onAccept={handleAccept}
                onRetry={handleRetry}
                onRegenerate={handleRegenerate}
                onCancel={() => {
                  aiCancel();
                  setSuggestionMode(false);
                  setFeedbackHistory([]);
                  setEditedText("");
                  aiReset();
                }}
              />
            ) : (
              !disabled && (
                <button
                  type="button"
                  onClick={handleGenerate}
                  className="border-border bg-background-secondary/50 hover:bg-hover/50 flex w-full items-center gap-1.5 border-t px-2 py-1.5 transition-colors"
                >
                  <AIBadge size={14} />
                  <span className="text-muted-foreground text-caption">
                    Suggest...
                  </span>
                </button>
              )
            )}
          </div>
          {hasError && (
            <p
              id={errorId}
              className="text-status-error-text mt-1.5 text-caption"
            >
              {errorMessage}
            </p>
          )}
        </>
      );
    }

    // Standard input without AI features
    return (
      <>
        <div className="relative">
          <input
            ref={ref}
            type={isPassword ? typeState : type}
            className={cx(
              inputStyles({ hasError, enableStepper }),
              {
                "pl-8": !!Icon,
                "pr-10": isPassword,
              },
              className,
            )}
            onChange={handleChange}
            onBlur={handleBlur}
            value={shownValue}
            disabled={disabled}
            aria-describedby={describedBy}
            {...props}
            autoComplete={autocomplete}
          />
          {Icon && (
            <div
              className={cx(
                // base
                "pointer-events-none absolute bottom-0 left-2 flex h-full items-center justify-center",
                // text color
                "text-foreground-muted",
              )}
            >
              {Icon ? (
                <Icon className="size-[1.125rem] shrink-0" aria-hidden={true} />
              ) : (
                <RiSearchLine
                  className="size-[1.125rem] shrink-0"
                  aria-hidden="true"
                />
              )}
            </div>
          )}
          {isPassword && (
            <div
              className={cx(
                "absolute bottom-0 right-0 flex h-full items-center justify-center px-3",
              )}
            >
              <button
                aria-label="Change password visibility"
                className={cx(
                  // base
                  "h-fit w-fit rounded-sm outline-none transition-all",
                  // text
                  "text-foreground-muted",
                  // hover
                  "hover:text-muted-foreground",
                  focusRing,
                )}
                type="button"
                onClick={() => {
                  setTypeState(typeState === "password" ? "text" : "password");
                }}
              >
                <span className="sr-only">
                  {typeState === "password" ? "Show password" : "Hide password"}
                </span>
                {typeState === "password" ? (
                  <RiEyeFill aria-hidden="true" className="size-5 shrink-0" />
                ) : (
                  <RiEyeOffFill
                    aria-hidden="true"
                    className="size-5 shrink-0"
                  />
                )}
              </button>
            </div>
          )}
        </div>
        {hasError && (
          <p
            id={errorId}
            className="text-status-error-text mt-1.5 text-caption"
          >
            {errorMessage}
          </p>
        )}
      </>
    );
  },
);

Input.displayName = "Input";

export default Input;
export { inputStyles, type InputProps };
